import React from "react";
import { Pressable, TouchableOpacity } from "react-native";

import { CustomButton } from "../../components/CustomButton";
import { CustomIcon } from "../../components/CustomIcon";
import SelectionOption from "../../components/SelectionOption";

import { ROUTES } from "../../shared/constants/routes";

import {
  ANALYTICS_EVENT,
  ACTION_TYPE,
  TrackingContext,
} from "../types";

import { trackInteraction } from "../analyticsUtil";
import { useNavStore } from "../../store/navStore";

type WithTrackingProps = {
  disableTrack?: boolean;
  context?: TrackingContext;
};

export function withOnPressInterceptor<
  P extends { onPress?: ((...args: any[]) => void) | null }
>(
  WrappedComponent: React.ComponentType<P>,
  options?: { name?: string }
) {
  type Props = Omit<P, "onPress"> &
    WithTrackingProps & {
      onPress?: ((...args: any[]) => void) | null;
    };

  const ComponentWithInterceptor: React.FC<Props> = (props) => {
    const { onPress, disableTrack, context, ...rest } = props;
    const { getCurrentRoute } = useNavStore();
    const handlePress = (...args: any[]) => {
      onPress?.(...args);

      if (disableTrack) return;

      trackInteraction({
        event: ANALYTICS_EVENT.INTERACTION,
        action: ACTION_TYPE.CLICK,
        screen_name: getCurrentRoute()?.name as ROUTES,
        component: options?.name ?? WrappedComponent.displayName ?? WrappedComponent.name,
        context
      });
    };

    return (
      <WrappedComponent
        {...(rest as unknown as P)}
        onPress={handlePress}
      />
    );
  };

  ComponentWithInterceptor.displayName = `WithOnPressInterceptor(${
    WrappedComponent.displayName ||
    WrappedComponent.name ||
    "Component"
  })`;

  return ComponentWithInterceptor;
}

export const TrackedIcon = withOnPressInterceptor(CustomIcon, { name: "CustomIcon" });
export const TrackedButton = withOnPressInterceptor(CustomButton, { name: "CustomButton" });
export const TrackedPressable = withOnPressInterceptor(Pressable, { name: "Pressable" });
export const TrackedTouchable = withOnPressInterceptor(TouchableOpacity, { name: "Touchable" });
export const TrackedSelectionOption = withOnPressInterceptor(SelectionOption, { name: "SelectionOption" });
